import { useState, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Book as BookIcon,
  CalendarToday as CalendarTodayIcon,
  AttachFile as AttachFileIcon,
  Person as PersonIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { TABLES, RELATIONS, FK_NAMES, getModules, getCreditTypes, getCurrentSchoolYear, getSchoolYears, resolveSchoolYear } from '../lib/tables';
import SchoolYearSelect from '../components/SchoolYearSelect';
import SchoolYearBadge from '../components/SchoolYearBadge';
import Alert from '@mui/material/Alert';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function RequestsList() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [filterType, setFilterType] = useState('all');
  const [creditTypes, setCreditTypes] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);
  
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });

  const loadRequests = async () => {
    setLoading(true);
    try {
      const yearFilter = selectedYear === 'all' ? yearOptions : [selectedYear || getCurrentSchoolYear()];
      
      // incarcam doar cererile din anul selectat
      const { data, error } = await supabase
        .from(TABLES.credit_requests)
        .select(`
          *,
          ${RELATIONS.credit_types}!inner(id, nume_tip, culoare, validator_rol),
          ${RELATIONS.modules}(cod_module, nume_module),
          profiles_validator:${RELATIONS.profiles}!${FK_NAMES.credit_requests_id_validator}(nume, prenume)
        `)
        .eq('id_elev', profile.id)
        .in('an_scolar', yearFilter)
        .order('data_cerere', { ascending: false });

      if (error) throw error;

      setRequests(data || []);

      const { data: typesData } = await supabase
        .from(TABLES.credit_types)
        .select('*')
        .order('id');

      setCreditTypes(typesData || []);
      
      const stats = {
        total: data?.length || 0,
        pending: data?.filter(r => r.status === 'in_asteptare').length || 0,
        approved: data?.filter(r => r.status === 'validat').length || 0,
        rejected: data?.filter(r => r.status === 'respins').length || 0,
      };
      setStats(stats);

    } catch (err) {
      setError('Eroare la încărcarea cererilor');
      console.error('Error loading requests:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    getSchoolYears(TABLES.credit_requests).then((years) => {
      if (!mounted) return;
      setYearOptions(years);
      setSelectedYear((prev) => prev ?? resolveSchoolYear(years));
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    let intervalId = null;
    
    if (!selectedYear) return undefined;

    const loadRequestsSafe = async () => {
      if (!isMounted) return;
      await loadRequests();
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMounted) {
        loadRequestsSafe();
      }
    };
    
    loadRequestsSafe();
    
    intervalId = setInterval(() => {
      if (document.visibilityState === 'visible' && isMounted) {
        loadRequestsSafe();
      }
    }, 60000);
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      isMounted = false;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedYear]);

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDialogOpen(true);
  };

  const handleDownloadDocument = async (documentUrl) => {
    if (!documentUrl) {
      alert('Nu există document atașat');
      return;
    }

    try {
      const response = await fetch(documentUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `document_${selectedRequest?.id || 'credite'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Eroare la descărcarea documentului');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      'in_asteptare': { label: 'În așteptare', color: 'warning', icon: <HourglassEmptyIcon /> },
      'validat': { label: 'Validat', color: 'success', icon: <CheckCircleIcon /> },
      'respins': { label: 'Respins', color: 'error', icon: <CancelIcon /> },
    };

    const config = statusConfig[status] || { label: status, color: 'default' };

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
        sx={{ fontWeight: 'medium' }}
      />
    );
  };

  const getTypeChip = (type) => {
    if (!type) {
      return <Chip label="N/A" size="small" variant="outlined" />;
    }
    
    return (
      <Chip
        label={type.nume_tip}
        size="small"
        sx={{
          backgroundColor: `${type.culoare || '#4CAF50'}20`,
          color: type.culoare || '#4CAF50',
          borderColor: type.culoare || '#4CAF50',
          fontWeight: 'medium',
        }}
        variant="outlined"
      />
    );
  };

  const filteredRequests = () => {
    let filtered = requests;

    switch (tabValue) {
      case 1: filtered = filtered.filter(r => r.status === 'in_asteptare'); break;
      case 2: filtered = filtered.filter(r => r.status === 'validat'); break;
      case 3: filtered = filtered.filter(r => r.status === 'respins'); break;
      default: filtered = requests;
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(r => {
        return r.credit_types && r.credit_types.id === filterType;
      });
    }

    return filtered;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Cererile mele
          </Typography>
          {selectedYear && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SchoolYearSelect value={selectedYear} options={yearOptions} onChange={setSelectedYear} sx={{ minWidth: 160 }} />
              <SchoolYearBadge year={selectedYear} />
            </Box>
          )}
        </Box>
        <Typography variant="body1" color="text.secondary">
          Vezi și gestionează toate cererile tale de credite
        </Typography>
      </Box>

      {/* Stat Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%', mb: 4 }}>
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 }, minHeight: 120 }}>
            <Typography variant="h3" color="primary" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' }, lineHeight: 1.2 }}>
              {stats.total}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, textAlign: 'center' }}>
              Total Cereri
            </Typography>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 }, minHeight: 120 }}>
            <Typography variant="h3" color="warning.main" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' }, lineHeight: 1.2 }}>
              {stats.pending}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, textAlign: 'center' }}>
              În Așteptare
            </Typography>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 }, minHeight: 120 }}>
            <Typography variant="h3" color="success.main" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' }, lineHeight: 1.2 }}>
              {stats.approved}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, textAlign: 'center' }}>
              Validate
            </Typography>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 }, minHeight: 120 }}>
            <Typography variant="h3" color="error.main" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' }, lineHeight: 1.2 }}>
              {stats.rejected}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, textAlign: 'center' }}>
              Respinse
            </Typography>
          </Card>
        </Box>
      </Box>

      {/* Toolbar cu filtre */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} variant="scrollable" scrollButtons="auto" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Tab label={`Toate (${stats.total})`} />
            <Tab label={`În așteptare (${stats.pending})`} />
            <Tab label={`Validate (${stats.approved})`} />
            <Tab label={`Respinse (${stats.rejected})`} />
          </Tabs>

          {/* Dropdown pentru filtrare după tip credit */}
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel>Tip Credit</InputLabel>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              label="Tip Credit"
            >
              <MenuItem value="all">Toate tipurile</MenuItem>
              {creditTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: type.culoare || '#4CAF50',
                      }}
                    />
                    {type.nume_tip}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'flex-start' }, gap: 1, width: { xs: '100%', sm: 'auto' }, alignItems: 'center' }}>
          <Tooltip title="Reîncarcă">
            <IconButton onClick={loadRequests} sx={{ mr: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/new-request')}
          >
            Cerere Nouă
          </Button>
        </Box>
      </Box>

      {selectedYear && selectedYear !== 'all' && requests.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Nu ai activități în anul {selectedYear} încă.
        </Alert>
      )}

      {/* Tabel cereri */}
      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow>
              <TableCell>Activitate</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell>Modul</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data depunerii</TableCell>
              <TableCell>Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests().map((request) => (
              <TableRow key={request.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {request.nume_activitate}
                  </Typography>
                  {request.descriere && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {request.descriere.substring(0, 50)}...
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{getTypeChip(getCreditTypes(request))}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {getModules(request)?.cod_module}
                  </Typography>
                </TableCell>
                <TableCell>{getStatusChip(request.status)}</TableCell>
                <TableCell>
                  {new Date(request.data_cerere).toLocaleDateString('ro-RO')}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Vezi detalii">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(request)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {request.document_url && (
                      <Tooltip title="Descarcă document">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadDocument(request.document_url)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {filteredRequests().length === 0 && (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <HourglassEmptyIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Nu există cereri {tabValue > 0 ? 'în această categorie' : ''}
            </Typography>
            {tabValue === 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/new-request')}
                sx={{ mt: 2 }}
              >
                Creează prima cerere
              </Button>
            )}
          </Box>
        )}
      </TableContainer>

        {/* Dialog detalii cerere - REDESIGN ELEVI */}
        <Dialog 
          open={dialogOpen} 
          onClose={() => setDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: { 
              borderRadius: 3,
              overflow: 'hidden'
            }
          }}
        >
          {selectedRequest && (
            <>
              {/* Header cu gradient colorat bazat pe tipul cererii */}
              <DialogTitle sx={{ 
                p: 0,
                background: 'white',
                color: 'text.primary'
              }}>
                <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Detalii Cerere
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', rowGap: 1 }}>
                      <Chip 
                        label={`#${selectedRequest.id}`}
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          fontWeight: 'bold',
                          border: '1px solid rgba(255,255,255,0.3)',
                          backgroundColor: getCreditTypes(selectedRequest)?.culoare || '#667eea',
                        }}
                      />
                      {getTypeChip(getCreditTypes(selectedRequest), true)}
                      {getStatusChip(selectedRequest.status, true)}
                    </Box>
                  </Box>
                  <IconButton 
                    onClick={() => setDialogOpen(false)}
                    sx={{ 
                      color: 'text.primary'
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>

              <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 3 }}>
                  {/* Card principal - Activitate */}
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: '#f0f7ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <DescriptionIcon color="primary" />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Activitate
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {selectedRequest.nume_activitate}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {selectedRequest.descriere && (
                        <Box sx={{ 
                          mt: 2, 
                          p: 2, 
                          bgcolor: '#f8f9fa', 
                          borderRadius: 2,
                          borderLeft: '4px solid #4A90E2'
                        }}>
                          <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom>
                            Descriere activitate
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {selectedRequest.descriere}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {/* Grid informații */}
                  <Grid container spacing={3}>
                    {/* Coloana 1: Detalii cerere */}
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                            Detalii cerere
                          </Typography>
                          
                          <Box sx={{ '& > div': { mb: 2 } }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Modul
                              </Typography>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                mt: 0.5
                              }}>
                                <BookIcon fontSize="small" color="action" />
                                <Typography variant="body1" fontWeight="medium">
                                  {getModules(selectedRequest)?.cod_module} - {getModules(selectedRequest)?.nume_module}
                                </Typography>
                              </Box>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Data depunerii
                              </Typography>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                mt: 0.5
                              }}>
                                <CalendarTodayIcon fontSize="small" color="action" />
                                <Typography variant="body1">
                                  {new Date(selectedRequest.data_cerere).toLocaleDateString('ro-RO')}
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    {new Date(selectedRequest.data_cerere).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
                                  </Typography>
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Coloana 2: Document atașat / Validare */}
                    <Grid item xs={12} md={6}>
                      {selectedRequest.document_url && (
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                              Document atașat
                            </Typography>
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: '#f0f7ff', 
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: { xs: 'flex-start', sm: 'center' },
                              gap: { xs: 1, sm: 2 },
                              flexDirection: { xs: 'column', sm: 'row' }
                            }}>
                              <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                bgcolor: '#e3f2fd',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <AttachFileIcon color="primary" />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  Document confirmare
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Fișier atașat pentru verificare
                                </Typography>
                              </Box>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadDocument(selectedRequest.document_url)}
                                sx={{ 
                                  minWidth: 120,
                                  width: { xs: '100%', sm: 'auto' },
                                  background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)'
                                }}
                              >
                                Descarcă
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      )}
                    </Grid>
                  </Grid>

                  {/* Secțiune validare (dacă nu este în așteptare) */}
                  {selectedRequest.status !== 'in_asteptare' && (
                    <Card variant="outlined" sx={{ mt: 3 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                          Procesare cerere
                        </Typography>
                        
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: '#e8f5e9', 
                              borderRadius: 2,
                              borderLeft: '4px solid #4CAF50'
                            }}>
                              <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom>
                                Data și ora validării
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon color="success" />
                                <Typography variant="body1" fontWeight="medium">
                                  {new Date(selectedRequest.data_validare).toLocaleDateString('ro-RO')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(selectedRequest.data_validare).toLocaleTimeString('ro-RO')}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: '#f5f5f5', 
                              borderRadius: 2
                            }}>
                              <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom>
                                Validator responsabil
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon color="action" />
                                <Typography variant="body1" fontWeight="medium">
                                  {selectedRequest.profiles_validator
                                    ? `${selectedRequest.profiles_validator.prenume} ${selectedRequest.profiles_validator.nume}`
                                    : 'N/A'}
                                </Typography>
                                {selectedRequest.profiles_validator?.rol && (
                                  <Chip 
                                    label={selectedRequest.profiles_validator.rol}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>

                        {selectedRequest.motiv_respingere && (
                          <Box sx={{ 
                            mt: 3, 
                            p: 2, 
                            bgcolor: '#ffebee', 
                            borderRadius: 2,
                            borderLeft: '4px solid #f44336'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <CancelIcon color="error" />
                              <Typography variant="subtitle2" fontWeight="bold" color="error">
                                Motiv respingere
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {selectedRequest.motiv_respingere}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Timeline vizual */}
                  <Card variant="outlined" sx={{ mt: 3 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                        Istoric status
                      </Typography>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: '50%',
                          left: 0,
                          right: 0,
                          height: 2,
                          bgcolor: '#e0e0e0',
                          zIndex: 0
                        }
                      }}>
                        {/* Step 1: Depunere */}
                        <Box sx={{ 
                          position: 'relative', 
                          zIndex: 1,
                          textAlign: 'center'
                        }}>
                          <Box sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: '#4CAF50',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            mb: 1
                          }}>
                            <CheckCircleIcon />
                          </Box>
                          <Typography variant="caption" fontWeight="bold">
                            Depus
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {new Date(selectedRequest.data_cerere).toLocaleDateString('ro-RO')}
                          </Typography>
                        </Box>

                        {/* Step 2: Status curent */}
                        <Box sx={{ 
                          position: 'relative', 
                          zIndex: 1,
                          textAlign: 'center'
                        }}>
                          <Box sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: selectedRequest.status === 'in_asteptare' ? '#FFC107' : 
                                    selectedRequest.status === 'validat' ? '#4CAF50' : '#f44336',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            mb: 1
                          }}>
                            {selectedRequest.status === 'in_asteptare' ? <PendingIcon /> :
                            selectedRequest.status === 'validat' ? <CheckCircleIcon /> : <CancelIcon />}
                          </Box>
                          <Typography variant="caption" fontWeight="bold">
                            {selectedRequest.status === 'in_asteptare' ? 'În verificare' :
                            selectedRequest.status === 'validat' ? 'Aprobat' : 'Respins'}
                          </Typography>
                          {selectedRequest.data_validare && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {new Date(selectedRequest.data_validare).toLocaleDateString('ro-RO')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </DialogContent>

              {/* Butoane footer */}
              <DialogActions sx={{ 
                p: 3, 
                borderTop: 1, 
                borderColor: 'divider',
                justifyContent: 'space-between'
              }}>
                <Button 
                  onClick={() => setDialogOpen(false)}
                  variant="outlined"
                  startIcon={<CloseIcon />}
                >
                  Închide
                </Button>
                
                {selectedRequest.status === 'in_asteptare' && selectedRequest.document_url && (
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadDocument(selectedRequest.document_url)}
                    sx={{ 
                      background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)'
                    }}
                  >
                    Descarcă document
                  </Button>
                )}
              </DialogActions>
            </>
          )}
        </Dialog>
    </Container>
  );
}